import { useEffect, useRef } from 'react';
import { ScrollArea } from './ui/scroll-area';
import { Save } from 'lucide-react';

export const TranscriptDisplay = ({ 
  transcript, 
  interimTranscript, 
  fontSize, 
  lineSpacing,
  placeholder,
  isListening,
  lastSaved,
  t
}) => {
  const scrollRef = useRef(null);
  const contentRef = useRef(null);

  // Auto-scroll to bottom when new text arrives
  useEffect(() => {
    if (contentRef.current && scrollRef.current) {
      const scrollContainer = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [transcript, interimTranscript]);

  const fontSizeClasses = {
    small: 'text-lg',
    medium: 'text-2xl',
    large: 'text-3xl',
    xlarge: 'text-4xl',
  };

  const lineSpacingClasses = {
    tight: 'leading-snug',
    normal: 'leading-relaxed',
    loose: 'leading-loose',
    double: 'leading-[2.5]',
  };

  const hasContent = transcript || interimTranscript;

  // Format last saved time
  const formatLastSaved = (date) => {
    if (!date) return null;
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return t?.('autosave_just_now') || 'Just now';
    if (diff < 3600000) {
      const mins = Math.floor(diff / 60000);
      return `${mins} ${mins === 1 ? 'min' : 'mins'} ago`;
    }
    return date.toLocaleTimeString();
  };

  return (
    <div 
      className="relative flex-1 min-h-[300px] border border-border rounded-sm bg-card"
      data-testid="transcript-display"
    >
      {/* Recording indicator */}
      {isListening && (
        <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
          </span>
          <span className="text-xs font-medium text-red-500">REC</span>
        </div>
      )}

      {/* Auto-save indicator */}
      {lastSaved && hasContent && (
        <div className="absolute top-4 left-4 flex items-center gap-1.5 z-10 text-muted-foreground">
          <Save className="h-3 w-3" />
          <span className="text-xs">
            {t?.('autosave_label') || 'Auto-saved'}: {formatLastSaved(lastSaved)}
          </span>
        </div>
      )}

      <ScrollArea 
        ref={scrollRef}
        className="h-[400px] sm:h-[500px] p-6 sm:p-8"
      >
        <div 
          ref={contentRef}
          className={`font-transcript ${fontSizeClasses[fontSize]} ${lineSpacingClasses[lineSpacing]} ${lastSaved && hasContent ? 'pt-4' : ''}`}
        >
          {hasContent ? (
            <>
              <span className="text-foreground">{transcript}</span>
              {interimTranscript && (
                <span className="text-muted-foreground opacity-70">{interimTranscript}</span>
              )}
            </>
          ) : (
            <p className="text-muted-foreground italic">{placeholder}</p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
